import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

function SectionHeader({ title, to }) {
    return (
        <div className="flex items-baseline justify-between border-b-2 border-ink pb-2 mb-6">
            <h2 className="font-serif text-2xl font-bold">{title}</h2>
            <Link to={to} className="font-mono text-xs uppercase tracking-wider text-accent hover:text-ink transition-colors duration-150">
                View all →
            </Link>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                <p className="font-mono text-sm text-muted-foreground animate-pulse">Loading the newsroom…</p>
            </div>
        );
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="home-empty">
                <h2 className="font-serif text-3xl font-bold">Fetching the latest stories</h2>
                <p className="mt-3 text-muted-foreground">Articles are being pulled from The Guardian. Please check back in a moment.</p>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-page">
            {/* Hero + latest + sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8">
                    <ArticleCard article={featured} variant="hero" />
                </div>
                <div className="lg:col-span-4">
                    <div className="border-b-2 border-ink pb-2 mb-2">
                        <h2 className="font-serif text-xl font-bold">Latest News</h2>
                    </div>
                    <div>
                        {latest.slice(0, 6).map((a) => (
                            <ArticleCard key={a.id} article={a} variant="compact" />
                        ))}
                    </div>
                </div>
            </div>

            {/* Category sections + trending sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-16">
                <div className="lg:col-span-8 space-y-16">
                    {Object.entries(sections).map(([cat, arts]) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader title={CATEGORY_LABELS[cat] || cat} to={`/category/${cat}`} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
